/**
 * School admin → Activity log.
 *
 * Append-only, scoped to this school. Records who did what to whom: student
 * creation, role and status changes, classroom assignment, password resets and
 * session revocations.
 *
 * It deliberately never contains a password, a reset token or an answer key —
 * the server assembles each entry's metadata field by field rather than spreading
 * a request body, so nothing sensitive can arrive here by accident and then live
 * permanently in a table that is never deleted.
 */
import { useQuery } from '@tanstack/react-query';
import { apiGet } from '@/lib/api';
import { Card, StudentPage, StudentPageHeader } from '@/components/ds';
import { QueryState } from '@/components/admin/QueryState';

interface AuditEntry {
  id: string;
  action: string;
  createdAt: string;
  metadata: Record<string, unknown> | null;
  actorUser: { id: string; name: string } | null;
  targetUser: { id: string; name: string } | null;
}

/** Plain-English descriptions. An enum name is not a sentence an admin reads. */
const ACTION_LABEL: Record<string, string> = {
  TENANT_UPDATED: 'changed school settings',
  MEMBERSHIP_ROLE_CHANGED: 'changed a role',
  MEMBERSHIP_STATUS_CHANGED: 'changed access',
  STUDENT_CREATED: 'added a student',
  STUDENT_BULK_IMPORTED: 'imported a student list',
  STAFF_CREATED: 'added a staff member',
  CLASSROOM_CREATED: 'created a class',
  CLASSROOM_ASSIGNED: 'assigned teachers to a class',
  CLASSROOM_ENROLLED: 'enrolled students',
  PASSWORD_RESET_ISSUED: 'reset a password',
  SESSIONS_REVOKED: 'signed someone out everywhere',
  CREDENTIAL_MODE_CONVERTED: 'moved to a self-managed account',
  DEVELOPMENT_BYPASS_LOGIN: 'signed in using the development bypass',
  SUPPORT_ACCESS: 'opened this school’s console as CWY support',
};

/**
 * Changes recorded under a broader action, told apart by `metadata.event` —
 * class books are written as TENANT_UPDATED until the audit enum (a Postgres
 * type) gains actions of their own.
 */
const EVENT_LABEL: Record<string, (meta: Record<string, unknown>) => string> = {
  CLASS_BOOK_ADDED: (meta) =>
    `added the book “${String(meta.title ?? '')}”${meta.classroom ? ` to ${String(meta.classroom)}` : ''}`,
  CLASS_BOOK_REMOVED: (meta) => `removed the book “${String(meta.title ?? '')}”`,
};

function describe(entry: AuditEntry): string {
  const actor = entry.actorUser?.name ?? 'Someone';
  const event =
    typeof entry.metadata?.event === 'string' ? EVENT_LABEL[entry.metadata.event] : null;
  const action = event
    ? event(entry.metadata!)
    : (ACTION_LABEL[entry.action] ?? entry.action.toLowerCase().replace(/_/g, ' '));
  const target = entry.targetUser?.name;
  return target ? `${actor} ${action} — ${target}` : `${actor} ${action}`;
}

export function SchoolAuditPage() {
  const audit = useQuery({
    queryKey: ['tenant-admin', 'audit'],
    queryFn: () =>
      apiGet<{ entries: AuditEntry[]; nextCursor: string | null }>('/tenant-admin/audit?limit=100'),
  });

  return (
    <StudentPage>
      <StudentPageHeader
        title="Activity log"
        subtitle="Who did what in your school. This record cannot be edited or deleted."
      />

      <QueryState
        isPending={audit.isPending}
        error={audit.error}
        isEmpty={audit.data?.entries.length === 0}
        empty={<p className="text-sm text-ink-soft">Nothing recorded yet.</p>}
      >
      <div className="flex flex-col gap-1.5">
        {audit.data?.entries.map((entry) => {
          // Credential and bypass events are the ones a school should notice.
          const notable =
            entry.action === 'PASSWORD_RESET_ISSUED' ||
            entry.action === 'DEVELOPMENT_BYPASS_LOGIN' ||
            entry.action === 'SUPPORT_ACCESS';
          return (
            <Card key={entry.id} padding="10px 14px">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <span
                  className="text-sm"
                  style={{ color: notable ? 'var(--bin-orange-deep)' : 'var(--text-body)' }}
                >
                  {describe(entry)}
                </span>
                <span className="text-xs text-ink-faint">
                  {new Date(entry.createdAt).toLocaleString('en-IN', {
                    dateStyle: 'medium',
                    timeStyle: 'short',
                  })}
                </span>
              </div>
            </Card>
          );
        })}
      </div>
      </QueryState>
    </StudentPage>
  );
}
